import { useNavigate } from 'react-router-dom'
import { Handshake } from 'lucide-react'
import type { Account, Dataset, Opportunity, CompetitivePosition } from '@/types/entities'
import { SectionCard, EmptyState, Badge } from '@/components/ui'
import { money, date } from '@/lib/format'
import { contactById, contactName } from '@/lib/selectors'
import type { AccountDerived } from '../lib'

const COMP_TONE: Record<CompetitivePosition, 'ok' | 'info' | 'warn' | 'alert'> = {
  'Partner of Choice': 'ok',
  'Competitive Plus': 'info',
  'Competitive': 'warn',
  'Competitive Minus': 'alert',
}

export default function DealsTab({ d, data }: { account: Account; d: AccountDerived; data: Dataset }) {
  const navigate = useNavigate()
  const deals = d.opportunities

  if (!deals.length) {
    return (
      <SectionCard pad>
        <EmptyState icon={<Handshake size={36} />} title="No deals on this account" hint="Open opportunities — equity partnerships, acquisitions, 3PM mandates, and JVs — will appear here." />
      </SectionCard>
    )
  }

  return (
    <div className="aw-deal-grid">
      {deals.map((o: Opportunity) => {
        const primary = contactById(data, o.primaryContactId)
        return (
          <button key={o.id} className="aw-deal-card" onClick={() => navigate(`/opportunities/${o.id}`)}>
            <div className="aw-deal-top">
              <Badge tone="navy" variant="soft">{o.type}</Badge>
              <Badge tone="neutral" variant="outline">{o.salesType}</Badge>
            </div>
            <div className="aw-deal-name">{o.name}</div>
            {o.description && <p className="aw-deal-desc">{o.description}</p>}
            <div className="aw-deal-stats">
              <Stat label="Stage" value={o.salesStage} />
              <Stat label="Size" value={o.dealSize != null ? money(o.dealSize, { compact: true }) : '—'} />
              <Stat label="Est. Close" value={o.estCloseQuarter ?? (o.estCloseDate ? date(o.estCloseDate, 'short') : '—')} />
            </div>
            <div className="aw-deal-foot">
              {o.competitivePosition && <Badge tone={COMP_TONE[o.competitivePosition]} variant="soft" dot>{o.competitivePosition}</Badge>}
              {o.feeTerms && <span className="aw-deal-fee">{o.feeTerms}</span>}
              {primary && <span className="aw-deal-contact">{contactName(primary)}</span>}
              {o.sourceReferredBy && <span className="aw-deal-source">via {o.sourceReferredBy}</span>}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="aw-deal-stat">
      <span className="label aw-deal-stat-label">{label}</span>
      <span className="aw-deal-stat-value">{value}</span>
    </div>
  )
}
